import { Player } from './types';
import { convertHeight, convertWeight } from './unitConversion';

export interface ColumnConfig {
  key: string;
  label: string;
  defaultVisible: boolean;
  alwaysVisible?: boolean;
}

export interface CSVExportOptions {
  players: Player[];
  visibleColumns: Set<string>;
  columnConfigs: ColumnConfig[];
  isMetric: boolean;
  filename?: string;
  successMessage?: string;
  toastFunction?: (options: { title: string; description: string }) => void;
}

/**
 * Enterprise-grade CSV export utility for player data
 * Handles proper CSV escaping, field formatting, and file generation
 * 
 * @param options Export configuration options
 * @returns void (triggers download)
 */
export function exportPlayersToCSV(options: CSVExportOptions): void {
  const {
    players,
    visibleColumns,
    columnConfigs,
    isMetric,
    filename,
    successMessage,
    toastFunction
  } = options;

  // Get visible columns in order
  const visibleColumnConfigs = columnConfigs.filter(col => visibleColumns.has(col.key));
  
  // Create CSV header
  const headers = visibleColumnConfigs.map(col => col.label);
  
  // Create CSV rows
  const rows = players.map(player => {
    return visibleColumnConfigs.map(col => {
      let value = '';
      
      switch (col.key) {
        case 'name':
          value = player.name || '';
          break;
        case 'age':
          value = player.age ? player.age.toString() : '';
          break;
        case 'position':
          value = player.position && player.position.length > 0 
            ? player.position.join(', ') 
            : '';
          break;
        case 'specificPositions':
          value = player.specific_positions && player.specific_positions.length > 0 
            ? player.specific_positions.join('; ') 
            : '';
          break;
        case 'height':
          value = convertHeight(player.height, isMetric) || '';
          break;
        case 'weight':
          value = convertWeight(player.weight, isMetric) || '';
          break;
        case 'playingLevel':
          value = player.playing_level || '';
          break;
        case 'gpa':
          value = player.gpa || '';
          break;
        case 'hsGradYear':
          value = player.hs_grad_year ? player.hs_grad_year.toString() : '';
          break;
        case 'nationality':
          value = player.nationality || '';
          break;
        case 'internationalCaps':
          value = player.international_caps && player.international_caps.length > 0
            ? player.international_caps
                .filter((cap) => cap && typeof cap === 'object') // Filter out invalid cap objects
                .map((cap) => {
                  // Handle the actual data structure: [{"U15":"3"},{"U16":"3"}]
                  return Object.entries(cap).map(([level, caps]) => `${level}: ${caps}`);
                })
                .flat() // Flatten the array since we're mapping over entries
                .join('; ')
            : '';
          break;
        case 'previousClubs':
          value = player.previous_clubs && player.previous_clubs.length > 0
            ? player.previous_clubs.join(', ')
            : '';
          break;
        case 'universityGradYear':
          value = player.university_grad_year ? player.university_grad_year.toString() : '';
          break;
        case 'currentClub':
          value = player.current_club || '';
          break;
        default:
          value = '';
      }
      
      // Enterprise CSV escaping - handle quotes, commas, and newlines
      return escapeCSVValue(value);
    });
  });
  
  // Combine headers and rows
  const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  
  // Generate filename with timestamp if not provided
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const finalFilename = filename || `bloom-players-${timestamp}.csv`;
  
  // Create and trigger download
  downloadCSV(csvContent, finalFilename);
  
  // Show success message if toast function provided
  if (toastFunction) {
    const message = successMessage || `Exported ${players.length} players to ${finalFilename}`;
    toastFunction({
      title: "Export Successful",
      description: message,
    });
  }
}

/**
 * Properly escape CSV values according to RFC 4180 standard
 * @param value Raw string value
 * @returns Escaped CSV value
 */
function escapeCSVValue(value: string): string {
  if (!value) return '';
  
  // Convert to string and handle null/undefined
  const stringValue = String(value);
  
  // Check if escaping is needed
  const needsEscaping = stringValue.includes('"') || 
                       stringValue.includes(',') || 
                       stringValue.includes('\n') || 
                       stringValue.includes('\r');
  
  if (!needsEscaping) {
    return stringValue;
  }
  
  // Escape double quotes by doubling them
  const escapedValue = stringValue.replace(/"/g, '""');
  
  // Wrap in double quotes
  return `"${escapedValue}"`;
}

/**
 * Create and trigger CSV file download
 * @param csvContent The CSV content string
 * @param filename The filename for the download
 */
function downloadCSV(csvContent: string, filename: string): void {
  // Create blob with proper CSV MIME type and UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF'; // UTF-8 BOM for proper Excel handling
  const blob = new Blob([BOM + csvContent], { 
    type: 'text/csv;charset=utf-8;' 
  });
  
  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up object URL
  URL.revokeObjectURL(url);
}

/**
 * Generate a standardized filename for CSV exports
 * @param prefix File prefix (e.g., 'players', 'shortlist')
 * @param suffix Optional suffix (e.g., shortlist name)
 * @returns Formatted filename with timestamp
 */
export function generateCSVFilename(prefix: string, suffix?: string): string {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const cleanSuffix = suffix 
    ? '-' + suffix.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()
    : '';
  
  return `bloom-${prefix}${cleanSuffix}-${timestamp}.csv`;
} 